import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Wallet as WalletIcon, Plus,
  Gift, Copy, Check, Clock, CheckCircle2, XCircle, ShieldCheck, Share2,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { inr, timeFrom } from "../lib/format";
import { walletApi, referralApi } from "../lib/api";
import AccountAccordion from "../components/AccountAccordion";
import DepositFlow from "../components/DepositFlow";
import WithdrawFlow from "../components/WithdrawFlow";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";

const QUICK = [100, 500, 1000, 2000];
const MIN_DEP = 100;
const MAX_DEP = 99999;
const MANUAL_MAX_DEP = 1000000;

const WD_STATUS = {
  pending: { icon: Clock, cls: "text-amber-400 bg-amber-500/10", label: "Pending", sub: () => "Awaiting admin review" },
  processing: { icon: Clock, cls: "text-sky-400 bg-sky-500/10", label: "Processing", sub: () => "Bank transfer in progress" },
  approved: { icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-500/10", label: "Credited", sub: (w) => `${inr(w.amount)} sent to A/C ••••${String(w.accountNumber).slice(-4)}` },
  paid: { icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-500/10", label: "Credited", sub: (w) => `${inr(w.amount)} sent to A/C ••••${String(w.accountNumber).slice(-4)}` },
  rejected: { icon: XCircle, cls: "text-red-400 bg-red-500/10", label: "Rejected", sub: () => "Amount refunded to wallet" },
};
const wdDest = (w) => (w.method === "upi" || w.upiId) ? `UPI ${w.upiId}` : `A/C ••••${String(w.accountNumber).slice(-4)}`;
WD_STATUS.approved.sub = (w) => `${inr(w.amount)} sent to ${wdDest(w)}`;
WD_STATUS.paid.sub = WD_STATUS.approved.sub;
const UPI_STATUS = {
  created: { icon: Clock, cls: "text-amber-400 bg-amber-500/10", label: "Awaiting payment" },
  success: { icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-500/10", label: "Credited" },
  failure: { icon: XCircle, cls: "text-red-400 bg-red-500/10", label: "Failed" },
  expired: { icon: XCircle, cls: "text-red-400 bg-red-500/10", label: "Expired" },
  mismatch: { icon: Clock, cls: "text-amber-400 bg-amber-500/10", label: "Under review" },
};

export default function Wallet() {
  const { isAuthed, balance, withdraw, refresh, user } = useApp();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const depositMode = searchParams.get("tab") === "deposit";
  const withdrawMode = searchParams.get("tab") === "withdraw";
  const showForms = depositMode || withdrawMode;
  const [amount, setAmount] = useState("");
  const [wd, setWd] = useState({ amount: "", accountName: "", accountNumber: "", ifsc: "", upiId: "", method: "bank" });
  const [withdrawals, setWithdrawals] = useState([]);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [bank, setBank] = useState(null);
  const [md, setMd] = useState({ amount: "", utr: "", method: "IMPS" });
  const [myDeposits, setMyDeposits] = useState([]);
  const [upiDeposits, setUpiDeposits] = useState([]);
  const [upiBusy, setUpiBusy] = useState(false);
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [savedMax, setSavedMax] = useState(4);
  const [wagerLeft, setWagerLeft] = useState(0);
  const [wdSubmitting, setWdSubmitting] = useState(false);

  const loadWallet = async () => {
    try {
      const data = await walletApi.get();
      setWithdrawals(data.withdrawals || []);
      setWagerLeft(Number(data.wageringRemaining || 0));
    } catch (e) { console.error("Failed to load wallet", e); }
  };

  const loadManual = async () => {
    try {
      const data = await walletApi.manualDepositList();
      setMyDeposits(data.deposits || []);
    } catch (e) { console.error("Failed to load manual deposits", e); }
  };

  const loadUpi = async () => {
    try {
      const data = await walletApi.upiDeposits();
      setUpiDeposits(data.deposits || []);
    } catch (e) { console.error("Failed to load UPI deposits", e); }
  };

  const payWithUpi = async (n) => {
    if (upiBusy) return;
    setUpiBusy(true);
    try {
      const dep = await walletApi.upiCreate(n);
      if (!dep?.payment_url) throw new Error("No payment link");
      window.location.assign(dep.payment_url);
    } catch (e) {
      setUpiBusy(false);
      toast({ title: "Could not start UPI payment", description: e?.response?.data?.detail || "Please try again in a moment." });
    }
  };

  const loadSavedAccounts = async () => {
    try {
      const data = await walletApi.bankAccounts();
      setSavedAccounts(data.accounts || []);
      if (data.max) setSavedMax(data.max);
    } catch (e) { console.error("Failed to load saved accounts", e); }
  };

  const removeSavedAccount = async (id) => {
    try {
      await walletApi.deleteBankAccount(id);
      loadSavedAccounts();
      toast({ title: "Saved account removed" });
    } catch (e) { console.error("Failed to remove account", e); }
  };

  useEffect(() => {
    if (isAuthed) {
      loadWallet();
      loadManual();
      loadUpi();
      loadSavedAccounts();
      walletApi.bankDetails().then(setBank).catch(() => {});
      referralApi.get().then(setReferral).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, balance]);



  const submitManual = async () => {
    const n = Number(md.amount);
    if (!n || n < MIN_DEP) { toast({ title: `Minimum deposit is ${inr(MIN_DEP)}` }); return; }
    if (n > MANUAL_MAX_DEP) { toast({ title: `Maximum bank deposit is ${inr(MANUAL_MAX_DEP)}` }); return; }
    if (!md.utr || md.utr.trim().length < 6) { toast({ title: "Enter a valid UTR / reference number" }); return; }
    try {
      await walletApi.manualDeposit({ amount: n, utr: md.utr.trim(), method: md.method });
      setMd((p) => ({ amount: "", utr: "", method: p.method }));
      loadManual();
      toast({ title: "Request submitted ✅", description: "Balance is credited with a 5% bonus once admin verifies your transfer." });
    } catch (e) {
      toast({ title: "Could not submit", description: e?.response?.data?.detail || "Try again" });
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard?.writeText(String(text));
    toast({ title: `${label} copied` });
  };

  const doWithdraw = async () => {
    setWdSubmitting(true);
    try { await _doWithdraw(); } finally { setWdSubmitting(false); }
  };
  const _doWithdraw = async () => {
    const n = Number(wd.amount);
    if (!n || n <= 0) { toast({ title: "Enter a valid amount" }); return; }
    if (n > balance) { toast({ title: "Insufficient balance" }); return; }
    const isUpi = wd.method === "upi";
    if (isUpi) {
      if (!/^[a-z0-9][a-z0-9._-]{1,48}@[a-z][a-z0-9]{1,30}$/i.test(wd.upiId.trim())) { toast({ title: "Enter a valid UPI ID", description: "Example: name@okaxis or 98765xxxxx@ybl" }); return; }
    } else if (!wd.accountName || !wd.accountNumber || wd.ifsc.length !== 11) {
      toast({ title: "Complete bank details", description: "Name, account number and a valid 11-char IFSC are required." });
      return;
    }
    const res = await withdraw(isUpi
      ? { amount: n, upiId: wd.upiId.trim().toLowerCase(), accountName: wd.accountName }
      : { amount: n, accountName: wd.accountName, accountNumber: wd.accountNumber, ifsc: wd.ifsc.toUpperCase() });
    if (res.ok) {
      toast({ title: "Withdrawal placed successfully \ud83c\udfe6", description: `${inr(n)} \u00b7 you will receive it within 2 hours.` });
      setWd({ amount: "", accountName: "", accountNumber: "", ifsc: "", upiId: "", method: wd.method });
      loadWallet();
      loadSavedAccounts();
    } else {
      toast({ title: "Could not withdraw", description: res.error });
    }
  };

  const refLink = referral?.code ? `https://batballworld.com/?ref=${referral.code}` : "";
  const shareMessage = referral?.code
    ? `Join me on BatBallWorld 🏏 Sign up with my referral code ${referral.code} and we both get a bonus! ${refLink}`
    : "";

  const copyCode = () => {
    if (!referral?.code) return;
    navigator.clipboard?.writeText(shareMessage);
    setCopied(true);
    toast({ title: "Referral link copied!", description: "Your link + code are ready to paste anywhere." });
    setTimeout(() => setCopied(false), 1500);
  };

  const shareReferral = async () => {
    if (!referral?.code) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "BatBallWorld", text: shareMessage, url: refLink });
        return;
      } catch { /* user cancelled or unsupported — fall back to copy */ }
    }
    copyCode();
  };

  return (
    <div className={`${!showForms && isAuthed ? "relative min-h-[calc(100dvh-4rem)] bg-white" : ""}`} data-testid="wallet-page-bg">
    <div className={`max-w-4xl mx-auto ${!showForms && isAuthed ? "px-0 py-0" : "px-4 sm:px-6 py-10"}`}>
      {(depositMode || withdrawMode) && (
        <h1 className="font-display font-bold text-3xl mb-6">{depositMode ? "Deposit" : "Withdraw"}</h1>
      )}
      {isAuthed && Number(user?.pendingSignupBonus) > 0 && (
        <div className={`${!showForms ? "mx-4 mt-4" : ""} mb-5 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm font-semibold text-primary`} data-testid="pending-signup-bonus">
          Your welcome bonus unlocks with your first deposit
        </div>
      )}

      {!showForms && isAuthed && (
        <AccountAccordion user={user} balance={balance} />
      )}
      {!showForms && !isAuthed && (
        <div className="rounded-2xl border border-border bg-card p-8 text-center text-muted-foreground" data-testid="account-signin-prompt">
          Sign in to view your account.
        </div>
      )}

      {withdrawMode && (
      <div className="grid gap-6 md:grid-cols-2 mb-8">
        <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-primary/15 to-transparent p-6">
          <div className="text-sm text-muted-foreground">Available balance</div>
          <div className="font-display font-extrabold text-4xl mt-1">{inr(balance)}</div>
          <div className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-primary" /> Instant UPI top-up · secure checkout</div>
        </div>

        {referral && (
          <div className="rounded-2xl border border-fuchsia-500/25 bg-fuchsia-500/5 p-6" data-testid="refer-earn-card">
            <h3 className="font-display font-semibold flex items-center gap-2"><Gift className="h-4 w-4 text-fuchsia-400" /> Refer &amp; Earn</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Share your link. Your friend gets {inr(referral.refereeBonus)} on signup, and you earn {inr(referral.referrerBonus)} once they deposit {inr(referral.minDeposit || 2000)} or more.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-secondary px-3 py-2 font-display font-bold tracking-wider" data-testid="referral-code">{referral.code}</code>
              <Button size="icon" variant="outline" className="bg-white/5 border-white/15" onClick={copyCode} data-testid="referral-copy-btn">
                {copied ? <Check className="h-4 w-4 text-primary" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            {refLink && (
              <div className="mt-2 rounded-lg bg-black/20 px-3 py-2 text-[11px] text-muted-foreground break-all" data-testid="referral-link">{refLink}</div>
            )}
            <Button className="mt-3 w-full font-semibold bg-fuchsia-500 hover:bg-fuchsia-500/90 text-white" onClick={shareReferral} data-testid="referral-share-btn">
              <Share2 className="h-4 w-4 mr-1.5" /> Share link &amp; code
            </Button>
            <div className="mt-3 flex gap-4 text-sm">
              <span className="text-muted-foreground">Invited: <b className="text-foreground">{referral.count}</b></span>
              <span className="text-muted-foreground">Earned: <b className="text-primary">{inr(referral.earnings)}</b></span>
            </div>

            {referral.count > 0 && (
              <div className="mt-3 border-t border-fuchsia-500/15 pt-3" data-testid="referral-friends">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                  Your invites · {referral.depositedCount}/{referral.count} deposited
                </div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto no-scrollbar">
                  {referral.referred.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-sm" data-testid={`referral-friend-${i}`}>
                      <span className="truncate">{f.name}</span>
                      {f.deposited ? (
                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-primary/15 text-primary px-2 py-0.5 text-[11px] font-semibold">
                          <CheckCircle2 className="h-3 w-3" /> +{inr(referral.referrerBonus)}
                        </span>
                      ) : (
                        <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-400 px-2 py-0.5 text-[11px] font-semibold">
                          <Clock className="h-3 w-3" /> Awaiting deposit
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">You earn {inr(referral.referrerBonus)} when an invited friend makes their first deposit.</p>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {showForms && (
      <div id="withdraw-section" className="scroll-mt-20">
      {depositMode ? (
        <div className="max-w-lg">
          <DepositFlow key={bank && !bank.upi?.enabled ? "bank" : "upi"} minDep={MIN_DEP} manualMax={MANUAL_MAX_DEP} bank={bank} md={md} setMd={setMd} onManualSubmit={submitManual}
            onUpiPay={payWithUpi} upiBusy={upiBusy} copyText={copyText} />
          {upiDeposits.length > 0 && (
            <div className="mt-6 max-w-lg" data-testid="my-upi-deposits">
              <h4 className="font-display font-semibold text-sm mb-3">Your UPI deposits <span className="text-xs text-muted-foreground font-normal">(credited)</span></h4>
              <div className="rounded-2xl border border-border bg-card divide-y divide-border">
                {upiDeposits.map((d) => {
                  const s = UPI_STATUS[d.status] || UPI_STATUS.created;
                  return (
                    <div key={d.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`my-upi-row-${d.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold">{inr(d.amount)} {d.bonus > 0 && d.status === "success" && <span className="text-primary text-xs">+ {inr(d.bonus)}</span>}</div>
                        <div className="text-xs text-muted-foreground truncate">UPI · {d.upi_txn_id ? `UTR ${d.upi_txn_id}` : d.client_txn_id} · {timeFrom(d.created_at)}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {d.status === "created" && d.payment_url && (
                          <a href={d.payment_url} className="text-xs font-semibold text-primary hover:underline" data-testid={`my-upi-pay-${d.id}`}>Pay now</a>
                        )}
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`} data-testid={`my-upi-status-${d.id}`}>
                          <s.icon className="h-3.5 w-3.5" /> {s.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {myDeposits.length > 0 && (
            <div className="mt-6 max-w-lg" data-testid="my-manual-deposits">
              <h4 className="font-display font-semibold text-sm mb-3">Your bank deposit requests</h4>
              <div className="rounded-2xl border border-border bg-card divide-y divide-border">
                {myDeposits.map((d) => {
                  const s = WD_STATUS[d.status] || WD_STATUS.pending;
                  return (
                    <div key={d.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`my-md-row-${d.id}`}>
                      <div className="min-w-0">
                        <div className="font-semibold">{inr(d.amount)} <span className="text-primary text-xs">+ {inr(d.bonus)}</span></div>
                        <div className="text-xs text-muted-foreground truncate">{d.method} · UTR {d.utr} · {timeFrom(d.created_at)}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>
                        <s.icon className="h-3.5 w-3.5" /> {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
      <div className="max-w-lg" data-testid="withdraw-section-body">
          <WithdrawFlow balance={balance} wagerLeft={wagerLeft} savedAccounts={savedAccounts} savedMax={savedMax}
            wd={wd} setWd={setWd} onSubmit={doWithdraw} submitting={wdSubmitting} />
          {withdrawals.length > 0 && (
            <div className="mt-6 max-w-lg">
              <h4 className="font-display font-semibold text-sm mb-3">Withdrawal requests</h4>
              <div className="rounded-2xl border border-border bg-card divide-y divide-border">
                {withdrawals.map((w) => {
                  const s = WD_STATUS[w.status] || WD_STATUS.pending;
                  return (
                    <div key={w.id} className="flex items-center justify-between px-5 py-3.5" data-testid={`wd-request-${w.id}`}>
                      <div>
                        <div className="font-semibold">{inr(w.amount)}</div>
                        <div className="text-xs text-muted-foreground">{(w.method === "upi" || w.upiId) ? `UPI ${w.upiId}` : `${w.accountName} · A/C ••${String(w.accountNumber).slice(-4)}`} · {timeFrom(w.at)}</div>
                        <div className={`text-[11px] mt-0.5 ${s.cls.split(" ")[0]}`} data-testid={`wd-request-sub-${w.id}`}>{s.sub(w)}</div>
                      </div>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`} data-testid={`wd-request-status-${w.id}`}>
                        <s.icon className="h-3.5 w-3.5" /> {s.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
      </div>
      )}
      </div>
      )}


    </div>
    </div>
  );
}
